import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';

interface SearchResult {
  workers: Array<{ id: string; name: string; role: string; phone: string; status: string; photo: string | null }>;
  projects: Array<{ id: string; code: string; name: string; status: string; postcode: string }>;
  contacts: Array<{ id: string; name: string; customer: string | null; role: string | null; contactType: string; phone: string }>;
}

export function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['search', q],
    queryFn: () => api.get<SearchResult>('/search', { params: { q } }).then((r) => r.data),
    enabled: q.length >= 2,
  });

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('mousedown', onClick); window.removeEventListener('keydown', onKey); };
  }, []);

  return (
    <div className="search-box" ref={wrapRef} style={{ position: 'relative' }}>
      <span style={{ color: 'var(--faint)', fontSize: 13 }}>⌕</span>
      <input ref={inputRef} placeholder="Search (/)" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} />
      {open && data && q.length >= 2 ? (
        <div className="notif-panel" style={{ width: 360, left: 'auto', right: 0 }}>
          {data.workers.length ? (
            <>
              <h4>Workers ({data.workers.length})</h4>
              {data.workers.map((w) => (
                <div key={w.id} className="notif-row" onClick={() => { navigate(`/workers?wid=${w.id}`); setOpen(false); }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{w.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>{w.role} · {w.phone} · {w.status}</div>
                  </div>
                </div>
              ))}
            </>
          ) : null}
          {data.projects.length ? (
            <>
              <h4>Projects ({data.projects.length})</h4>
              {data.projects.map((p) => (
                <div key={p.id} className="notif-row" onClick={() => { navigate(`/projects?pid=${p.id}`); setOpen(false); }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{p.code} · {p.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>{p.postcode} · {p.status}</div>
                  </div>
                </div>
              ))}
            </>
          ) : null}
          {data.contacts.length ? (
            <>
              <h4>Contacts ({data.contacts.length})</h4>
              {data.contacts.map((c) => (
                <div key={c.id} className="notif-row" onClick={() => { navigate(`/contacts`); setOpen(false); }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>{c.role ?? ''} · {c.customer ?? ''} · {c.contactType}</div>
                  </div>
                </div>
              ))}
            </>
          ) : null}
          {!data.workers.length && !data.projects.length && !data.contacts.length ? <div style={{ fontSize: 11, color: 'var(--muted)', padding: 8 }}>No matches.</div> : null}
        </div>
      ) : null}
    </div>
  );
}
